import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Loader, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import api from '../services/api';

// Landing page for the link in a team-invite email (POST /admin/team/invite
// → TeamMember model). That flow only ever created a TeamMember document
// with no real login account and no page to set a password on — this is
// the missing piece: takes the invite token from the URL, lets the invitee
// pick a password, and hands off to /auth/accept-team-invitation, which
// creates/activates the real User account and logs them straight in.
export default function AcceptTeamInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This invitation link is missing its token — please use the link from your invite email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/accept-team-invitation', { token, password });
      localStorage.setItem('token', res.data.accessToken);
      setDone(true);
      setTimeout(() => { window.location.href = '/dashboard'; }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not accept this invitation. It may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 space-y-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col items-center gap-3">
          <BrandLogo size={40} />
          <div className="text-center">
            <h1 className="text-xl font-bold text-[#080707]">Join Your Team</h1>
            <p className="text-[13px] text-[#706E6B] mt-1">Set a password to activate your account.</p>
          </div>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 size={36} className="text-[#2E844A]" />
            <p className="text-[14px] font-bold text-[#080707]">Account activated — signing you in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-[#FDE7E9] border border-[#BA0517]/20 rounded text-[13px] text-[#BA0517]">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">New Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full h-9 pl-9 pr-10 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B]">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">Confirm Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-9 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader size={15} className="animate-spin" /> : null}
              {loading ? 'Activating…' : 'Activate Account'}
            </button>

            <p className="text-center text-[12px] text-[#706E6B]">
              Already have an account? <Link to="/login" className="text-[#0176D3] font-bold hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
